import { useCallback, useEffect, useRef, useState } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import AdminMenu from '../components/AdminMenu';

/**
 * Admin → Repo Health page.
 *
 * Configures the weekly automated job that analyzes each listed repository's
 * recently-merged PRs for risk, renders a PDF per repo, and emails each repo's
 * PDF to that repo's OWN recipient list on a cron schedule.
 *
 * Backed by:
 *   GET/PUT /api/repo-health/config
 *   GET     /api/repo-health/config/export   (download settings as .json)
 *   POST    /api/repo-health/config/import   (upload a settings .json)
 *   GET     /api/repo-health/status
 *   POST    /api/repo-health/run-now      { dryRun }
 *   POST    /api/repo-health/verify-smtp
 *
 * SMTP credentials live in server env (SMTP_HOST/USER/PASS/…), never here.
 */

// A few friendly cron presets so admins don't have to know cron syntax.
const SCHEDULE_PRESETS = [
    { label: 'Every Monday 8:00 AM', value: '0 8 * * 1' },
    { label: 'Every Friday 4:00 PM', value: '0 16 * * 5' },
    { label: 'Daily 8:00 AM', value: '0 8 * * *' },
    { label: '1st of month 8:00 AM', value: '0 8 1 * *' },
];

const DOW_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// US timezones offered in the dropdown. IANA names auto-handle daylight saving
// (e.g. America/Los_Angeles -> PST in winter, PDT in summer). Arizona + Hawaii
// don't observe DST, which is why they're listed separately.
const US_TIMEZONES = [
    { label: 'Eastern (New York)', value: 'America/New_York' },
    { label: 'Central (Chicago)', value: 'America/Chicago' },
    { label: 'Mountain (Denver)', value: 'America/Denver' },
    { label: 'Mountain — no DST (Phoenix)', value: 'America/Phoenix' },
    { label: 'Pacific (Los Angeles)', value: 'America/Los_Angeles' },
    { label: 'Alaska (Anchorage)', value: 'America/Anchorage' },
    { label: 'Hawaii (Honolulu)', value: 'Pacific/Honolulu' },
];

/** Add an ordinal suffix: 1 -> "1st", 2 -> "2nd", 21 -> "21st". */
function ordinal(n) {
    const s = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
}

/** Format cron's numeric minute+hour as a 12-hour clock like "4:00 PM". */
function formatClock(minStr, hourStr) {
    const min = Number(minStr);
    const hour = Number(hourStr);
    if (!Number.isInteger(min) || !Number.isInteger(hour) || hour > 23 || min > 59) return null;
    const ampm = hour < 12 ? 'AM' : 'PM';
    const h12 = hour % 12 === 0 ? 12 : hour % 12;
    return `${h12}:${String(min).padStart(2, '0')} ${ampm}`;
}

/** Short timezone abbreviation (e.g. "PST", "EDT") for an IANA tz, right now. */
function tzAbbrev(timeZone) {
    try {
        const parts = new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'short' })
            .formatToParts(new Date());
        return parts.find(p => p.type === 'timeZoneName')?.value || '';
    } catch {
        return '';
    }
}

/**
 * Translate a 5-field cron string into a human-readable schedule, e.g.
 *   describeCron('0 21 * * 4', 'America/Los_Angeles') -> "Thu at 9:00 PM PST"
 * Handles the common weekly / daily / monthly shapes; returns null when the
 * fields are too complex (lists, ranges, steps) to describe confidently.
 */
function describeCron(cron, timeZone) {
    if (typeof cron !== 'string') return null;
    const parts = cron.trim().split(/\s+/);
    if (parts.length !== 5) return null;
    const [min, hour, dom, mon, dow] = parts;

    const clock = formatClock(min, hour);
    if (!clock) return null; // only describe single-value time fields

    const tz = tzAbbrev(timeZone);
    const tzSuffix = tz ? ` ${tz}` : '';

    // Weekly — a specific day-of-week wins (cron allows 0 or 7 for Sunday).
    if (/^[0-7]$/.test(dow) && dom === '*') {
        return `${DOW_NAMES[Number(dow) % 7]} at ${clock}${tzSuffix}`;
    }
    // Monthly — a specific day-of-month, every month.
    if (/^\d{1,2}$/.test(dom) && Number(dom) >= 1 && Number(dom) <= 31 && mon === '*' && dow === '*') {
        return `Monthly on the ${ordinal(Number(dom))} at ${clock}${tzSuffix}`;
    }
    // Daily — no day constraints at all.
    if (dom === '*' && dow === '*' && mon === '*') {
        return `Daily at ${clock}${tzSuffix}`;
    }
    // Recognized time but an unusual day pattern — still show the time.
    return `At ${clock}${tzSuffix}`;
}

/** Short human label for what triggered a run. */
function triggerLabel(trigger, dryRun) {
    if (trigger === 'cron') return 'Scheduled';
    if (trigger === 'manual-dry' || dryRun) return 'Dry run';
    if (trigger === 'manual') return 'Manual';
    return trigger || 'run';
}

/** Colored pill style per trigger kind. */
function triggerBadge(trigger) {
    const base = {
        fontSize: '10px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.04em',
        padding: '2px 7px', borderRadius: '10px', whiteSpace: 'nowrap',
    };
    if (trigger === 'cron') return { ...base, background: '#0e2a3a', color: '#7fd0ff', border: '1px solid #1b4a63' };
    if (trigger === 'manual-dry') return { ...base, background: '#2a2030', color: '#d7a8ff', border: '1px solid #4a3263' };
    return { ...base, background: '#1f2a16', color: '#bfe39a', border: '1px solid #3a5226' }; // manual
}

export default function AdminRepoHealth() {
    const { oktaAuth } = useOktaAuth() || {};
    const [config, setConfig] = useState(null);
    const [status, setStatus] = useState(null);
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const [running, setRunning] = useState(false);
    // Which single repo is mid-run (for the per-repo "Run" button spinner).
    const [runningRepo, setRunningRepo] = useState('');

    // Single-line entry fields for the add-to-list inputs.
    const [repoDraft, setRepoDraft] = useState('');
    // Per-repo email draft, keyed by "owner/repo" so each card has its own input.
    const [emailDrafts, setEmailDrafts] = useState({});
    // Which repo cards are expanded. Collapsed by default so the page shows just
    // the repository list; the recipient editor reveals on click.
    const [expandedRepos, setExpandedRepos] = useState({});
    // Draft for the global "always send to" list.
    const [alwaysDraft, setAlwaysDraft] = useState('');
    // GitHub repo picker (same source as the PR report form): /api/github/repos.
    const [repoOptions, setRepoOptions] = useState([]);
    const [loadingRepos, setLoadingRepos] = useState(false);
    const [repoError, setRepoError] = useState('');
    // Import/export of the whole settings file. importing drives the button
    // spinner; the hidden <input type=file> is triggered by the Import button.
    const [importing, setImporting] = useState(false);
    const importInputRef = useRef(null);

    const authHeader = useCallback(async () => {
        let token = '';
        try { token = oktaAuth ? await oktaAuth.getAccessToken() : ''; } catch { /* dev */ }
        return token ? { Authorization: `Bearer ${token}` } : {};
    }, [oktaAuth]);

    const fetchAll = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const headers = await authHeader();
            const [cfgRes, statRes, logRes] = await Promise.all([
                fetch('/api/repo-health/config', { headers }),
                fetch('/api/repo-health/status', { headers }),
                fetch('/api/repo-health/logs?limit=25', { headers }),
            ]);
            const cfgBody = await cfgRes.json().catch(() => ({}));
            const statBody = await statRes.json().catch(() => ({}));
            const logBody = await logRes.json().catch(() => ({}));
            if (!cfgRes.ok || !cfgBody.success) throw new Error(cfgBody.error || `HTTP ${cfgRes.status}`);
            setConfig(cfgBody.config);
            if (statRes.ok && statBody.success) setStatus(statBody.status);
            if (logRes.ok && logBody.success) setLogs(logBody.runs || []);
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    }, [authHeader]);

    useEffect(() => { fetchAll(); }, [fetchAll]);

    const save = useCallback(async () => {
        if (!config) return;
        setSaving(true);
        setError('');
        setNotice('');
        try {
            const headers = { ...(await authHeader()), 'Content-Type': 'application/json' };
            const res = await fetch('/api/repo-health/config', {
                method: 'PUT',
                headers,
                body: JSON.stringify({ config }),
            });
            const body = await res.json().catch(() => ({}));
            if (!res.ok || !body.success) throw new Error(body.error || `HTTP ${res.status}`);
            setConfig(body.config);
            setNotice('Saved. Scheduler re-armed.');
            fetchAll();
        } catch (e) {
            setError(e.message);
        } finally {
            setSaving(false);
        }
    }, [config, authHeader, fetchAll]);

    const runNow = useCallback(async (dryRun, repo) => {
        setRunning(true);
        if (repo) setRunningRepo(repo);
        setError('');
        setNotice('');
        try {
            const headers = { ...(await authHeader()), 'Content-Type': 'application/json' };
            const res = await fetch('/api/repo-health/run-now', {
                method: 'POST',
                headers,
                body: JSON.stringify(repo ? { dryRun, repo } : { dryRun }),
            });
            const body = await res.json().catch(() => ({}));
            if (!res.ok || !body.success) throw new Error(body.error || body.result?.error || `HTTP ${res.status}`);
            const r = body.result;
            const lines = (r.repos || []).map(x => {
                if (x.error) return `• ${x.repo}: ⚠️ ${x.error}`;
                // Always surface the count (0 included) so it's consistent with the
                // critical/high figures and 0 isn't confused with missing data.
                const stats = `${x.totalPRs} PR(s), ${x.critical} critical, ${x.high} high, ${x.fastApprovals ?? 0} fast-approved`;
                if (dryRun) return `• ${x.repo}: ${stats}`;
                const mail = x.emailed
                    ? `emailed ${x.recipientCount} recipient(s) ✅`
                    : `NOT emailed: ${x.emailError || 'unknown reason'}`;
                return `• ${x.repo}: ${stats} — ${mail}`;
            });
            const scope = repo ? ` for ${repo}` : '';
            const head = dryRun
                ? `Dry run complete${scope} (no email sent).`
                : `Run complete${scope} — emailed ${r.emailedRepos ?? 0} repo(s).`;
            setNotice(`${head}\n${lines.join('\n')}`);
            fetchAll();
        } catch (e) {
            setError(e.message);
        } finally {
            setRunning(false);
            setRunningRepo('');
        }
    }, [authHeader, fetchAll]);

    const verifySmtp = useCallback(async () => {
        setError('');
        setNotice('');
        try {
            const headers = { ...(await authHeader()), 'Content-Type': 'application/json' };
            const res = await fetch('/api/repo-health/verify-smtp', { method: 'POST', headers });
            const body = await res.json().catch(() => ({}));
            if (!res.ok || !body.success) throw new Error(body.error || `HTTP ${res.status}`);
            setNotice(body.message || 'SMTP verified.');
        } catch (e) {
            setError(`SMTP verify failed: ${e.message}`);
        }
    }, [authHeader]);

    // Export the CURRENT (in-memory) settings as a downloadable JSON file so
    // they can be re-imported on another instance (e.g. local -> server)
    // without re-entering every repo + recipient by hand. Done client-side so
    // it captures unsaved edits too; the file holds no secrets.
    const exportConfig = useCallback(() => {
        if (!config) return;
        setError('');
        setNotice('');
        try {
            const blob = new Blob([JSON.stringify(config, null, 2) + '\n'], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `repo-health-config-${new Date().toISOString().slice(0, 10)}.json`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            setNotice('Exported settings to a downloaded .json file.');
        } catch (e) {
            setError(`Export failed: ${e.message}`);
        }
    }, [config]);

    // Read a previously-exported .json file and upload it to the server, which
    // normalises + persists it and re-arms the scheduler.
    const importConfig = useCallback(async (file) => {
        if (!file) return;
        setImporting(true);
        setError('');
        setNotice('');
        try {
            const text = await file.text();
            let parsed;
            try {
                parsed = JSON.parse(text);
            } catch {
                throw new Error('That file is not valid JSON.');
            }
            const headers = { ...(await authHeader()), 'Content-Type': 'application/json' };
            const res = await fetch('/api/repo-health/config/import', {
                method: 'POST',
                headers,
                body: JSON.stringify({ config: parsed }),
            });
            const body = await res.json().catch(() => ({}));
            if (!res.ok || !body.success) throw new Error(body.error || `HTTP ${res.status}`);
            setConfig(body.config);
            const s = body.summary || {};
            setNotice(`Imported settings: ${s.repositories ?? 0} repo(s), ${s.alwaysRecipients ?? 0} always-recipient(s). Scheduler re-armed.`);
            fetchAll();
        } catch (e) {
            setError(`Import failed: ${e.message}`);
        } finally {
            setImporting(false);
        }
    }, [authHeader, fetchAll]);

    const onImportFilePicked = useCallback((e) => {
        const file = e.target.files?.[0];
        e.target.value = ''; // allow re-picking the same file
        importConfig(file);
    }, [importConfig]);

    // Pull the org/user repo list from the same endpoint the PR report form uses.
    const fetchRepoOptions = useCallback(async () => {
        setLoadingRepos(true);
        setRepoError('');
        try {
            const headers = await authHeader();
            const res = await fetch('/api/github/repos', { headers });
            const body = await res.json().catch(() => ({}));
            if (!res.ok || !body.success) throw new Error(body.error || body.message || `HTTP ${res.status}`);
            const names = (body.repos || body.repositories || [])
                .map(r => r.full_name || r.fullName || r.name || '')
                .filter(Boolean)
                .sort((a, b) => a.localeCompare(b));
            setRepoOptions([...new Set(names)]);
            if (body.warning) setRepoError(body.warning);
        } catch (e) {
            setRepoError(`Could not load repositories: ${e.message}. You can still type owner/repo manually.`);
        } finally {
            setLoadingRepos(false);
        }
    }, [authHeader]);

    // Load the repo list once on mount so the picker is ready.
    useEffect(() => { fetchRepoOptions(); }, [fetchRepoOptions]);

    // ── immutable list helpers ──────────────────────────────────────────────
    // Each repository is { repo: "owner/repo", recipients: ["a@x.com", …] }.
    const addRepo = (value) => {
        const raw = (value ?? repoDraft) || '';
        const v = raw.trim().replace(/^https?:\/\/github\.com\//i, '').replace(/\.git$/i, '');
        if (!v) return;
        setConfig(c => {
            const repos = c.repositories || [];
            if (repos.some(r => r.repo === v)) return c; // no dupes
            return { ...c, repositories: [...repos, { repo: v, recipients: [] }] };
        });
        setRepoDraft('');
    };
    const removeRepo = (repo) =>
        setConfig(c => ({ ...c, repositories: c.repositories.filter(r => r.repo !== repo) }));

    const toggleRepo = (repo) =>
        setExpandedRepos(s => ({ ...s, [repo]: !s[repo] }));

    const setEmailDraft = (repo, value) =>
        setEmailDrafts(d => ({ ...d, [repo]: value }));

    const addEmailToRepo = (repo, email) => {
        const v = (email ?? emailDrafts[repo] ?? '').trim().toLowerCase();
        if (!v) return;
        setConfig(c => ({
            ...c,
            repositories: c.repositories.map(r =>
                r.repo === repo
                    ? { ...r, recipients: [...new Set([...(r.recipients || []), v])] }
                    : r,
            ),
        }));
        setEmailDraft(repo, '');
    };
    const removeEmailFromRepo = (repo, email) =>
        setConfig(c => ({
            ...c,
            repositories: c.repositories.map(r =>
                r.repo === repo
                    ? { ...r, recipients: r.recipients.filter(e => e !== email) }
                    : r,
            ),
        }));

    // "Always send to" — global recipients added to EVERY repo's email.
    const addAlwaysEmail = (email) => {
        const v = (email ?? alwaysDraft).trim().toLowerCase();
        if (!v) return;
        setConfig(c => ({ ...c, alwaysRecipients: [...new Set([...(c.alwaysRecipients || []), v])] }));
        setAlwaysDraft('');
    };
    const removeAlwaysEmail = (email) =>
        setConfig(c => ({ ...c, alwaysRecipients: (c.alwaysRecipients || []).filter(e => e !== email) }));

    if (loading && !config) return <div style={styles.page}><h2>🩺 Lyric Repository Health Manager</h2><p style={styles.help}>Loading…</p></div>;
    if (error && !config) return <div style={styles.page}><h2>🩺 Lyric Repository Health Manager</h2><div style={styles.error}>⚠️ {error}</div></div>;
    if (!config) return null;

    return (
        <div style={styles.page}>
            <div style={styles.pageHeader}>
                <h2 style={{ margin: 0 }}>🩺 Lyric Repository Health Manager</h2>
                <AdminMenu />
            </div>
            <p style={styles.subtitle}>
                Automatically analyze each repository’s recently-merged PRs for risk, render a PDF, and email it to the
                recipients on a schedule. Reuses the same risk engine and PDF renderer as the manual PR report.
            </p>

            {error && <div style={styles.error}>⚠️ {error}</div>}
            {notice && <div style={styles.success}>{notice}</div>}

            {/* STATUS ----------------------------------------------------------- */}
            {status && (
                <section style={styles.section}>
                    <h3 style={styles.h3}>Status</h3>
                    <div style={styles.statusGrid}>
                        <Stat label="Scheduler" value={status.schedulerRunning ? 'Running' : (status.enabled ? 'Armed' : 'Disabled')} ok={status.schedulerRunning || status.enabled} />
                        <Stat label="SMTP" value={status.smtpConfigured ? 'Configured' : 'Not configured'} ok={status.smtpConfigured} />
                        <Stat label="GitHub token" value={status.githubTokenPresent ? 'Present' : 'Missing'} ok={status.githubTokenPresent} />
                        <Stat label="Repositories" value={String(status.repositories?.length ?? 0)} ok={(status.repositories?.length ?? 0) > 0} />
                        <Stat label="Unique recipients" value={String(status.recipientCount ?? 0)} ok={(status.recipientCount ?? 0) > 0} />
                        <Stat label="Always send to" value={String(status.alwaysRecipientCount ?? 0)} ok={(status.alwaysRecipientCount ?? 0) > 0} />
                        <Stat label="Last run" value={status.lastRun ? new Date(status.lastRun).toLocaleString() : 'never'} ok={!!status.lastRun} />
                    </div>
                    {!status.smtpConfigured && (
                        <p style={styles.help}>
                            ⚠️ Email is off until SMTP env vars are set on the server (<code>SMTP_HOST</code>, and
                            <code> SMTP_USER</code>/<code>SMTP_PASS</code> if your relay needs auth). You can still use
                            <strong> Run now (dry run)</strong> to build PDFs without sending.
                        </p>
                    )}
                </section>
            )}

            {/* RECENT RUNS ------------------------------------------------------ */}
            <section style={styles.section}>
                <div style={styles.runsHeader}>
                    <h3 style={{ ...styles.h3, marginBottom: 0 }}>Recent runs</h3>
                    <button onClick={fetchAll} disabled={loading} style={styles.btn}>↻ Refresh</button>
                </div>
                {logs.length === 0
                    ? <p style={styles.help}>No runs recorded yet. Use <strong>Run now (dry run)</strong> below, then come back here.</p>
                    : (
                        <div style={styles.runList}>
                            {logs.map((run, i) => (
                                <details key={`${run.timestamp}-${i}`} style={styles.runItem} open={i === 0}>
                                    <summary style={styles.runSummary}>
                                        <span style={styles.runIcon}>{run.success ? (run.failedRepos ? '⚠️' : '✅') : '❌'}</span>
                                        <span style={styles.runWhen}>{run.timestamp ? new Date(run.timestamp).toLocaleString() : '—'}</span>
                                        <span style={triggerBadge(run.trigger)}>{triggerLabel(run.trigger, run.dryRun)}</span>
                                        <span style={styles.runMeta}>
                                            {run.success
                                                ? `${run.repoCount} repo(s) · ${run.emailedRepos} emailed${run.failedRepos ? ` · ${run.failedRepos} failed` : ''}`
                                                : (run.error || 'failed')}
                                            {Number.isFinite(run.durationMs) ? ` · ${(run.durationMs / 1000).toFixed(1)}s` : ''}
                                        </span>
                                    </summary>
                                    {(run.repos || []).length > 0 && (
                                        <ul style={styles.runRepoList}>
                                            {run.repos.map((r, j) => (
                                                <li key={`${r.repo}-${j}`} style={styles.runRepoLine}>
                                                    <code style={styles.runRepoName}>{r.repo}</code>
                                                    {r.error
                                                        ? <span style={styles.runRepoErr}>⚠️ {r.error}</span>
                                                        : <span style={styles.runRepoStats}>
                                                            {r.totalPRs} PR(s) · {r.critical} critical · {r.high} high
                                                            {' · '}
                                                            {run.dryRun
                                                                ? <em style={{ color: '#9aa7b8' }}>dry run</em>
                                                                : r.emailed
                                                                    ? <span style={{ color: '#81c784' }}>emailed {r.recipientCount}</span>
                                                                    : <span style={{ color: '#ffb74d' }}>not emailed{r.emailError ? `: ${r.emailError}` : ''}</span>}
                                                        </span>}
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </details>
                            ))}
                        </div>
                    )}
            </section>

            {/* SCHEDULE --------------------------------------------------------- */}
            <section style={styles.section}>
                <h3 style={styles.h3}>Schedule</h3>
                <div style={styles.row}>
                    <label style={styles.checkLabel}>
                        <input
                            type="checkbox"
                            checked={!!config.enabled}
                            onChange={(e) => setConfig(c => ({ ...c, enabled: e.target.checked }))}
                        />
                        <span>Enabled (run automatically on the schedule)</span>
                    </label>
                </div>
                <div style={styles.row}>
                    <label style={styles.label}>
                        <span style={styles.labelRow}>
                            Cron schedule
                            {describeCron(config.schedule, config.timezone)
                                ? <span style={styles.cronHuman}>{describeCron(config.schedule, config.timezone)}</span>
                                : <span style={styles.cronHumanWarn}>unrecognized pattern</span>}
                        </span>
                        <input
                            type="text"
                            value={config.schedule}
                            onChange={(e) => setConfig(c => ({ ...c, schedule: e.target.value }))}
                            style={{ ...styles.input, fontFamily: 'monospace' }}
                            placeholder="0 8 * * 1"
                        />
                    </label>
                    <label style={styles.label}>
                        Preset
                        <select
                            value={SCHEDULE_PRESETS.some(p => p.value === config.schedule) ? config.schedule : ''}
                            onChange={(e) => e.target.value && setConfig(c => ({ ...c, schedule: e.target.value }))}
                            style={styles.input}
                        >
                            <option value="">— choose —</option>
                            {SCHEDULE_PRESETS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                        </select>
                    </label>
                    <label style={styles.label}>
                        <span style={styles.labelRow}>
                            Timezone
                            {tzAbbrev(config.timezone) && <span style={styles.cronHuman}>{tzAbbrev(config.timezone)}</span>}
                        </span>
                        <select
                            value={US_TIMEZONES.some(t => t.value === config.timezone) ? config.timezone : ''}
                            onChange={(e) => setConfig(c => ({ ...c, timezone: e.target.value }))}
                            style={styles.input}
                        >
                            {!US_TIMEZONES.some(t => t.value === config.timezone) && (
                                <option value="">{config.timezone || '— choose —'}</option>
                            )}
                            {US_TIMEZONES.map(t => (
                                <option key={t.value} value={t.value}>
                                    {t.label}{tzAbbrev(t.value) ? ` — ${tzAbbrev(t.value)}` : ''}
                                </option>
                            ))}
                        </select>
                    </label>
                    <label style={styles.label}>
                        Lookback (days)
                        <input
                            type="number" min="1" max="90"
                            value={config.lookbackDays}
                            onChange={(e) => setConfig(c => ({ ...c, lookbackDays: Number(e.target.value) || 7 }))}
                            style={styles.input}
                        />
                    </label>
                </div>
                <p style={styles.help}>Cron format: <code>minute hour day-of-month month day-of-week</code>. Example <code>0 8 * * 1</code> = Mondays at 08:00.</p>
            </section>

            {/* REPOSITORIES & PER-REPO RECIPIENTS ------------------------------- */}
            <section style={styles.section}>
                <h3 style={styles.h3}>Repositories &amp; recipients</h3>
                <p style={styles.help}>
                    Each repository has its own recipient list. When the job runs, every repo’s PDF is emailed
                    only to the people listed under that repo.
                </p>
                <div style={styles.addRow}>
                    <input
                        type="text"
                        list="repo-health-repo-options"
                        value={repoDraft}
                        onChange={(e) => setRepoDraft(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addRepo(); } }}
                        style={{ ...styles.input, marginTop: 0, fontFamily: 'monospace' }}
                        placeholder={loadingRepos ? '⏳ Loading repositories…' : '🔍 Search repositories, or type owner/repo'}
                    />
                    <datalist id="repo-health-repo-options">
                        {repoOptions
                            .filter(name => !(config.repositories || []).some(r => r.repo === name))
                            .map(name => <option key={name} value={name} />)}
                    </datalist>
                    <button onClick={() => addRepo()} style={styles.btn}>+ Add repository</button>
                    <button onClick={fetchRepoOptions} disabled={loadingRepos} style={styles.btn} title="Reload repository list">
                        {loadingRepos ? '…' : '↻'}
                    </button>
                </div>
                <p style={styles.help}>
                    {repoError
                        ? <span style={{ color: '#ffb74d' }}>⚠️ {repoError}</span>
                        : `${repoOptions.length} repositories available from GitHub (${process.env.REACT_APP_GITHUB_ORG || 'ischemaview'} + your account). Start typing to filter.`}
                </p>

                {(config.repositories || []).length === 0
                    ? <p style={styles.help}>No repositories yet. Search above or type <code>owner/repo</code>.</p>
                    : (
                        <div style={styles.repoList}>
                            {config.repositories.map(r => {
                                const recipientCount = (r.recipients || []).length;
                                const expanded = !!expandedRepos[r.repo];
                                return (
                                <div key={r.repo} style={styles.repoCard}>
                                    <div style={styles.repoHeader}>
                                        <button
                                            type="button"
                                            onClick={() => toggleRepo(r.repo)}
                                            style={styles.repoToggle}
                                            title={expanded ? 'Collapse' : 'Expand'}
                                            aria-expanded={expanded}
                                        >
                                            <span style={styles.repoChevron}>{expanded ? '▾' : '▸'}</span>
                                            <code style={styles.repoName}>{r.repo}</code>
                                            <span style={styles.repoCountBadge}>
                                                {recipientCount} recipient{recipientCount === 1 ? '' : 's'}
                                            </span>
                                        </button>
                                        <div style={styles.repoActions}>
                                            <button
                                                onClick={() => runNow(false, r.repo)}
                                                disabled={running}
                                                style={styles.btnRun}
                                                title={`Build ${r.repo}'s report and email it now`}
                                            >
                                                {runningRepo === r.repo ? '…' : '▶ Run'}
                                            </button>
                                            <button onClick={() => removeRepo(r.repo)} style={styles.btnRemove} title="Remove repository">
                                                ✕ Remove repo
                                            </button>
                                        </div>
                                    </div>

                                    {expanded && (
                                      <>
                                        <div style={styles.addRow}>
                                            <EmailTypeahead
                                                value={emailDrafts[r.repo] || ''}
                                                onChange={(v) => setEmailDraft(r.repo, v)}
                                                onSelect={(email) => addEmailToRepo(r.repo, email)}
                                                onEnter={() => addEmailToRepo(r.repo)}
                                                authHeader={authHeader}
                                                name={`rh-recipient-${r.repo}`}
                                                placeholder="Type a name or email to search contacts…"
                                            />
                                            <button onClick={() => addEmailToRepo(r.repo)} style={styles.btnAdd}>+ Add recipient</button>
                                        </div>

                                        {recipientCount === 0
                                            ? <p style={styles.help}>
                                                No repo-specific recipients.
                                                {(config.alwaysRecipients || []).length > 0
                                                    ? ' This report still goes to the “Always send to” list below.'
                                                    : ' This report won’t be emailed to anyone.'}
                                              </p>
                                            : (
                                                <div style={styles.chips}>
                                                    {r.recipients.map(e => (
                                                        <span key={e} style={styles.chip}>
                                                            {e}
                                                            <button onClick={() => removeEmailFromRepo(r.repo, e)} style={styles.chipX} title="Remove">✕</button>
                                                        </span>
                                                    ))}
                                                </div>
                                            )}
                                      </>
                                    )}
                                </div>
                                );
                            })}
                        </div>
                    )}
            </section>

            {/* ALWAYS SEND TO --------------------------------------------------- */}
            <section style={styles.section}>
                <h3 style={styles.h3}>Always send to</h3>
                <p style={styles.help}>
                    These people receive <strong>every</strong> repository’s report, in addition to each repo’s own
                    recipients above. Handy for a manager or team lead. Duplicates are de-duplicated automatically.
                </p>
                <div style={styles.addRow}>
                    <EmailTypeahead
                        value={alwaysDraft}
                        onChange={setAlwaysDraft}
                        onSelect={(email) => addAlwaysEmail(email)}
                        onEnter={() => addAlwaysEmail()}
                        authHeader={authHeader}
                        name="rh-always-recipient"
                        placeholder="Type a name or email to search contacts…"
                    />
                    <button onClick={() => addAlwaysEmail()} style={styles.btn}>+ Add</button>
                </div>
                {(config.alwaysRecipients || []).length === 0
                    ? <p style={styles.help}>No always-recipients yet.</p>
                    : (
                        <div style={styles.chips}>
                            {config.alwaysRecipients.map(e => (
                                <span key={e} style={styles.chip}>
                                    {e}
                                    <button onClick={() => removeAlwaysEmail(e)} style={styles.chipX} title="Remove">✕</button>
                                </span>
                            ))}
                        </div>
                    )}
            </section>

            {/* EMAIL ------------------------------------------------------------ */}
            <section style={styles.section}>
                <h3 style={styles.h3}>Email</h3>
                <div style={styles.row}>
                    <label style={styles.label}>
                        From address (optional — defaults to server SMTP_FROM)
                        <input
                            type="text"
                            value={config.email?.from || ''}
                            onChange={(e) => setConfig(c => ({ ...c, email: { ...c.email, from: e.target.value } }))}
                            style={styles.input}
                            placeholder="repo-health@example.com"
                        />
                    </label>
                    <label style={styles.label}>
                        Subject prefix
                        <input
                            type="text"
                            value={config.email?.subjectPrefix || ''}
                            onChange={(e) => setConfig(c => ({ ...c, email: { ...c.email, subjectPrefix: e.target.value } }))}
                            style={styles.input}
                            placeholder="[Repo Health]"
                        />
                    </label>
                </div>
            </section>

            {/* ACTIONS ---------------------------------------------------------- */}
            <section style={styles.actions}>
                <button onClick={save} disabled={saving} style={styles.btnPrimary}>
                    {saving ? 'Saving…' : '💾 Save & re-arm'}
                </button>
                <button onClick={() => runNow(true)} disabled={running} style={styles.btn}>
                    {running ? '…' : '🧪 Run now (dry run)'}
                </button>
                <button onClick={() => runNow(false)} disabled={running} style={styles.btn}>
                    {running ? '…' : '📨 Run now & email'}
                </button>
                <button onClick={verifySmtp} style={styles.btn}>✅ Verify SMTP</button>
                <button onClick={fetchAll} disabled={loading} style={styles.btn}>↻ Reload</button>
                <span style={{ width: '1px', alignSelf: 'stretch', background: '#2f3a44' }} />
                <button onClick={exportConfig} disabled={!config} style={styles.btn} title="Download these settings as a .json file">
                    ⬇️ Export settings
                </button>
                <button onClick={() => importInputRef.current?.click()} disabled={importing} style={styles.btn} title="Upload a settings .json file to replace the current config">
                    {importing ? 'Importing…' : '⬆️ Import settings'}
                </button>
                <input
                    ref={importInputRef}
                    type="file"
                    accept="application/json,.json"
                    onChange={onImportFilePicked}
                    style={{ display: 'none' }}
                />
                <span style={{ flex: 1 }} />
                <small style={{ color: '#666' }}>
                    Last updated: {config.lastUpdatedBy} · {config.lastUpdatedAt ? new Date(config.lastUpdatedAt).toLocaleString() : '—'}
                </small>
            </section>
        </div>
    );
}

function Stat({ label, value, ok }) {
    return (
        <div style={styles.statCard}>
            <div style={{ ...styles.statValue, color: ok ? '#81c784' : '#ef9a9a' }}>{value}</div>
            <div style={styles.statLabel}>{label}</div>
        </div>
    );
}

/**
 * Email input with a contact typeahead backed by /api/jira/user-search (the
 * ischemaview Atlassian directory). Type ≥2 chars → a debounced search shows
 * matching people; pick one (click / ↑↓+Enter) to insert their email, or just
 * type a full address and press Enter to add it verbatim.
 */
function EmailTypeahead({ value, onChange, onSelect, onEnter, authHeader, placeholder, name }) {
    const [suggestions, setSuggestions] = useState([]);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [activeIdx, setActiveIdx] = useState(-1);
    const boxRef = useRef(null);
    const debounceRef = useRef(null);
    const reqIdRef = useRef(0);

    useEffect(() => {
        const q = (value || '').trim();
        if (q.length < 2) { setSuggestions([]); setOpen(false); return undefined; }
        clearTimeout(debounceRef.current);
        const myReq = ++reqIdRef.current;
        debounceRef.current = setTimeout(async () => {
            setLoading(true);
            try {
                const headers = await authHeader();
                const res = await fetch(`/api/jira/user-search?query=${encodeURIComponent(q)}&maxResults=8`, { headers });
                const body = await res.json().catch(() => ({}));
                if (myReq !== reqIdRef.current) return; // a newer keystroke superseded this response
                const users = body.users || [];
                setSuggestions(users);
                setOpen(true);
                setActiveIdx(-1);
            } catch {
                if (myReq === reqIdRef.current) { setSuggestions([]); }
            } finally {
                if (myReq === reqIdRef.current) setLoading(false);
            }
        }, 250);
        return () => clearTimeout(debounceRef.current);
    }, [value, authHeader]);

    // Close the dropdown when clicking outside the widget.
    useEffect(() => {
        const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, []);

    const pick = (u) => { onSelect(u.email); setOpen(false); setSuggestions([]); };

    const onKeyDown = (e) => {
        if (open && suggestions.length) {
            if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx(i => Math.min(i + 1, suggestions.length - 1)); return; }
            if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx(i => Math.max(i - 1, 0)); return; }
            if (e.key === 'Enter' && activeIdx >= 0) { e.preventDefault(); pick(suggestions[activeIdx]); return; }
            if (e.key === 'Escape') { e.preventDefault(); setOpen(false); return; }
        }
        if (e.key === 'Enter') { e.preventDefault(); onEnter(); }
    };

    return (
        <div ref={boxRef} style={{ position: 'relative', flex: 1, minWidth: 0 }}>
            <input
                // type="text" (NOT "email") on purpose: Chrome ignores
                // autoComplete="off" for type="email" and treats every email box
                // on the page as one contact group, filling an address into all
                // of them at once. text + inputMode="email" keeps the email
                // keyboard on mobile without the browser's contact autofill.
                type="text"
                inputMode="email"
                name={name}
                autoComplete="off"
                data-lpignore="true"
                data-1p-ignore="true"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                onKeyDown={onKeyDown}
                onFocus={() => { if (suggestions.length) setOpen(true); }}
                style={{ ...styles.input, marginTop: 0, width: '100%', boxSizing: 'border-box' }}
                placeholder={placeholder}
            />
            {open && (loading || suggestions.length > 0) && (
                <div style={styles.typeaheadMenu}>
                    {suggestions.map((u, i) => (
                        <div
                            key={u.accountId || u.email}
                            onMouseDown={(e) => { e.preventDefault(); pick(u); }}
                            onMouseEnter={() => setActiveIdx(i)}
                            style={{ ...styles.typeaheadItem, ...(i === activeIdx ? styles.typeaheadItemActive : {}) }}
                        >
                            {u.avatarUrl
                                ? <img src={u.avatarUrl} alt="" style={styles.typeaheadAvatar} />
                                : <span style={styles.typeaheadAvatarFallback}>{(u.displayName || u.email).charAt(0).toUpperCase()}</span>}
                            <span style={styles.typeaheadName}>{u.displayName}</span>
                            <span style={styles.typeaheadEmail}>{u.email}</span>
                        </div>
                    ))}
                    {loading && <div style={styles.typeaheadLoading}>Searching contacts…</div>}
                    {!loading && suggestions.length === 0 && (
                        <div style={styles.typeaheadLoading}>No matches — press Enter to add as typed.</div>
                    )}
                </div>
            )}
        </div>
    );
}

const styles = {
    page: { padding: '24px', maxWidth: '1100px', margin: '0 auto', color: '#fff' },
    pageHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', marginBottom: '8px' },
    subtitle: { color: '#aaa', marginTop: 0, lineHeight: 1.5 },
    section: { marginTop: '20px', padding: '16px', borderRadius: '6px', background: '#1f2429', border: '1px solid #2f3a44' },
    h3: { marginTop: 0, marginBottom: '8px', color: '#4fc3f7' },
    help: { color: '#888', fontSize: '0.85rem', marginTop: '8px' },
    row: { display: 'flex', gap: '16px', flexWrap: 'wrap', marginTop: '8px' },
    label: { display: 'flex', flexDirection: 'column', flex: '1 1 180px', minWidth: '140px', color: '#ddd', fontSize: '0.85rem' },
    labelRow: { display: 'flex', alignItems: 'baseline', gap: '8px', flexWrap: 'wrap' },
    cronHuman: { color: '#81c784', fontSize: '0.8rem', fontWeight: 700 },
    cronHumanWarn: { color: '#ffb74d', fontSize: '0.8rem', fontWeight: 600 },
    checkLabel: { display: 'flex', alignItems: 'center', gap: '8px', color: '#ddd', fontSize: '0.9rem', cursor: 'pointer' },
    input: {
        marginTop: '4px', height: '38px', padding: '0 10px', borderRadius: '4px',
        border: '1px solid #444', background: '#161a1e', color: '#fff',
        width: '100%', boxSizing: 'border-box',
    },
    addRow: { display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'nowrap' },
    typeaheadMenu: {
        position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 20,
        background: '#161a1e', border: '1px solid #2a3a52', borderRadius: '6px',
        boxShadow: '0 8px 24px rgba(0,0,0,0.45)', maxHeight: '260px', overflowY: 'auto',
    },
    typeaheadItem: {
        display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 10px',
        cursor: 'pointer', borderBottom: '1px solid #20262d',
    },
    typeaheadItemActive: { background: '#22304a' },
    typeaheadAvatar: { width: '22px', height: '22px', borderRadius: '50%', flexShrink: 0 },
    typeaheadAvatarFallback: {
        width: '22px', height: '22px', borderRadius: '50%', flexShrink: 0,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        background: '#2a3a52', color: '#cfe1ff', fontSize: '11px', fontWeight: 700,
    },
    typeaheadName: { color: '#e6edf3', fontSize: '13px', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flexShrink: 0, maxWidth: '45%' },
    typeaheadEmail: { color: '#8aa0b6', fontSize: '12px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginLeft: 'auto' },
    typeaheadLoading: { padding: '8px 10px', color: '#8aa0b6', fontSize: '12px' },
    repoList: { display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '12px' },
    repoCard: { display: 'flex', flexDirection: 'column', gap: '12px', background: '#161a1e', border: '1px solid #2a3a52', borderRadius: '8px', padding: '12px 14px' },
    repoHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap' },
    repoToggle: {
        display: 'flex', alignItems: 'center', gap: '10px', flex: 1, minWidth: 0,
        background: 'transparent', border: 'none', padding: '2px 0', cursor: 'pointer',
        textAlign: 'left',
    },
    repoChevron: { color: '#7fa8d0', fontSize: '12px', width: '12px', flexShrink: 0 },
    repoActions: { display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 },
    repoCountBadge: {
        fontSize: '11px', fontWeight: 700, color: '#9fb3c8',
        background: '#101722', border: '1px solid #2a3a52', borderRadius: '10px',
        padding: '1px 8px', whiteSpace: 'nowrap',
    },
    repoName: { fontSize: '14px', fontWeight: 700, color: '#cfe1ff' },
    chips: { display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '12px' },
    chip: {
        display: 'inline-flex', alignItems: 'center', gap: '8px',
        background: '#162132', border: '1px solid #2a3a52', borderRadius: '16px',
        padding: '4px 6px 4px 12px', fontSize: '13px', color: '#cfe1ff',
    },
    chipX: {
        background: 'transparent', border: 'none', color: '#7fa8d0',
        cursor: 'pointer', fontSize: '12px', lineHeight: 1, padding: '2px 4px',
    },
    statusGrid: { display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '8px' },
    statCard: { flex: '1 1 120px', minWidth: '110px', background: '#161a1e', border: '1px solid #2f3a44', borderRadius: '6px', padding: '10px 12px' },
    statValue: { fontSize: '15px', fontWeight: 800 },
    statLabel: { fontSize: '10px', color: '#8aa', textTransform: 'uppercase', letterSpacing: '0.04em', marginTop: '2px' },
    runsHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', marginBottom: '8px' },
    runList: { display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '4px' },
    runItem: { background: '#161a1e', border: '1px solid #2f3a44', borderRadius: '6px', padding: '8px 10px' },
    runSummary: { display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', flexWrap: 'wrap', listStyle: 'none' },
    runIcon: { fontSize: '14px' },
    runWhen: { fontSize: '13px', fontWeight: 700, color: '#dbe4f0', minWidth: '160px' },
    runMeta: { fontSize: '12px', color: '#9aa7b8' },
    runRepoList: { listStyle: 'none', margin: '8px 0 2px', padding: '8px 0 0', borderTop: '1px solid #2a313a', display: 'flex', flexDirection: 'column', gap: '4px' },
    runRepoLine: { display: 'flex', gap: '10px', alignItems: 'baseline', flexWrap: 'wrap', fontSize: '12px' },
    runRepoName: { color: '#cfe1ff', fontWeight: 700 },
    runRepoStats: { color: '#c2cbd6' },
    runRepoErr: { color: '#ef9a9a' },
    actions: { display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginTop: '20px' },
    btn: {
        height: '38px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        padding: '0 14px', borderRadius: '4px', border: '1px solid #3f4a55',
        background: '#2a2f36', color: '#e5e7eb', cursor: 'pointer', fontSize: '13px', fontWeight: 600,
        whiteSpace: 'nowrap', boxSizing: 'border-box',
    },
    btnPrimary: {
        height: '38px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        padding: '0 16px', borderRadius: '4px', border: '1px solid #1a56db',
        background: '#1a56db', color: '#fff', cursor: 'pointer', fontSize: '13px', fontWeight: 700,
        whiteSpace: 'nowrap', boxSizing: 'border-box',
    },
    btnRun: {
        height: '38px', flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        padding: '0 16px', borderRadius: '4px', border: '1px solid #2e7d32',
        background: '#1f5128', color: '#d7f5d9', cursor: 'pointer', fontSize: '13px', fontWeight: 700,
        whiteSpace: 'nowrap', boxSizing: 'border-box',
    },
    btnRemove: {
        height: '38px', flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        padding: '0 14px', borderRadius: '4px', border: '1px solid #3f4a55',
        background: '#2a2f36', color: '#e5e7eb', cursor: 'pointer', fontSize: '13px', fontWeight: 600,
        whiteSpace: 'nowrap', boxSizing: 'border-box',
    },
    // Sits next to the recipient input — same 38px height so the row reads as one
    // clean line, wide enough to read, and never shrinks the input away.
    btnAdd: {
        height: '38px', flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        padding: '0 18px', borderRadius: '4px', border: '1px solid #3f4a55',
        background: '#2a2f36', color: '#e5e7eb', cursor: 'pointer', fontSize: '13px', fontWeight: 600,
        whiteSpace: 'nowrap', boxSizing: 'border-box',
    },
    error: { backgroundColor: '#3a1a1a', color: '#ef9a9a', padding: 10, borderRadius: 4, marginTop: 12, fontSize: 13, whiteSpace: 'pre-wrap' },
    success: { backgroundColor: '#0e2a1a', color: '#a5d6a7', padding: 10, borderRadius: 4, marginTop: 12, fontSize: 13, whiteSpace: 'pre-wrap' },
};
